import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';

import type {
  AssinaturaAcordoPainel,
  DirecionamentoPayload,
  HistoricoDevedorRow,
  NegociacaoAiInsights,
  NegociacaoCasoDetail,
  NegociacaoCasoTelefoneUi,
} from '../types/negociacaoDetailTypes';
import type {
  CertificadoNegociacaoResumoDto,
  CertificadoNegociacaoState,
  ConversasConhecidasDoCaso,
  ErroDoCertificadoNegociacao,
  PaginaDaSecaoDoCertificado,
  PaginaRecebidaDoCertificado,
  SecaoCarregadaDoCertificado,
  SecoesDoCertificadoState,
} from '../types/certificadoNegociacaoTypes';
import type { MarcosState } from '../types/negociacaoMarcoTypes';
import { idsDasConversasDaFila, mensagemEhDoCaso } from '../utils/acontecimentosDoCertificado';
import { ehVinculoAReconciliar } from '../utils/certificadoNegociacao';
import {
  addMensagem,
  direcionamentoFailed,
  direcionamentoPendenciaCreated,
  direcionamentoResolved,
  negociacaoCasoStatusUpdated,
  resumeConversaAutomation,
  updateConversa,
  updateMensagemStatus,
} from '@/features/messaging';
import type { Flow } from '@/features/negociacaoIndividualWizard';
import {
  closeNegociacaoCaso,
  pauseNegociacaoCaso,
  resumeNegociacaoCaso,
  SITUACAO_KEY_INTERVENCAO_HUMANA,
} from '@/features/negociacoes';
import {
  bootstrapNegociacaoDetail,
  cancelNegociacaoStart,
  downloadNegociacaoReport,
  encerrarAtendimentoHumanoDetail,
  encerrarNegociacaoDetail,
  fetchAssinaturaAcordo,
  fetchFluxoDaNegociacao,
  fetchNegociacaoAiInsights,
  fetchNegociacaoCasoDetail,
  fetchNegociacaoConversaId,
  fetchNegociacaoMarcos,
  fetchNegociacaoTelefones,
  fetchOutrasNegociacoes,
  obterEmissaoAtualDoCertificado,
  reabrirNegociacaoDetail,
  selecionarDirecionamento,
  sendResumeTemplate,
  solicitarPdfDoCertificado,
  updateNegociacaoCaso,
  atualizarEmissaoDoCertificado,
  carregarPaginaDaSecaoDoCertificado,
} from './actions';

interface NegociacaoDetailState {
  caso: NegociacaoCasoDetail | null;
  marcos: MarcosState;
  /** O canal ATIVO do caso — a conversa do número que negocia agora. */
  conversaId: string | null;
  /**
   * O canal que a aba Chat está MOSTRANDO, que não é necessariamente o ativo: com fila de
   * telefones, o operador pode abrir a conversa de um número já tentado. Estado separado de
   * propósito — colapsar os dois é o que fazia o composer enviar para o número errado.
   */
  canalVisualizadoId: string | null;
  telefones: {
    items: NegociacaoCasoTelefoneUi[];
    isLoading: boolean;
    error: string | null;
    /**
     * O caso cuja fila já foi buscada — a guarda de "uma vez por caso".
     *
     * Sem ela a aba Chat usava `items.length > 0` como prova de carregamento, e caso legado
     * (fila vazia) nunca satisfazia a condição: o efeito rearmava a cada resposta e martelava
     * o endpoint. Preenchido também em falha, para erro não virar retry infinito.
     */
    casoIdBuscado: string | null;
  };
  aiInsights: NegociacaoAiInsights | null;

  isLoadingAiInsights: boolean;
  aiInsightsError: string | null;

  loading: boolean;
  error: string | null;

  isCancellingStart: boolean;
  cancelStartError: string | null;

  isUpdating: boolean;
  updateError: string | null;

  isDownloadingReport: boolean;
  downloadReportError: string | null;

  isEncerrando: boolean;
  encerramentoError: string | null;
  isEncerrandoAtendimento: boolean;
  encerrarAtendimentoError: string | null;
  isReabrindo: boolean;
  reaberturaError: string | null;

  isResumingJanela: boolean;
  resumeJanelaError: string | null;
  resumeJanelaCaseId: string | null;

  lexiProcessing: boolean;
  lexiProcessingFailed: boolean;

  assinaturaAcordo: {
    casoId: string | null;
    requestId: string | null;
    data: AssinaturaAcordoPainel | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
  };

  fluxo: {
    flowId: string | null;
    requestId: string | null;
    data: Flow | null;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
  };

  /** A emissão do certificado do caso aberto; nunca a URL do PDF, que é assinada e expira. */
  certificado: CertificadoNegociacaoState;

  outrasNegociacoes: {
    items: HistoricoDevedorRow[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    isLoading: boolean;
    error: string | null;
  };
}

const marcosVazios = (): MarcosState => ({
  casoId: null,
  requestId: null,
  items: [],
  nextCursor: null,
  hasMore: false,
  status: 'idle',
  error: null,
  tentativas: 0,
});

const secaoNaoIniciada = <T>(): SecaoCarregadaDoCertificado<T> => ({
  items: [],
  total: 0,
  nextCursor: null,
  hasMore: false,
  iniciada: false,
});

const secoesDaEmissao = (emissaoId: string | null): SecoesDoCertificadoState => ({
  emissaoId,
  requestId: null,
  carregando: false,
  falhou: null,
  campos: secaoNaoIniciada(),
  contatos: secaoNaoIniciada(),
  marcos: secaoNaoIniciada(),
  mensagens: secaoNaoIniciada(),
});

function acrescentarPagina<T>(
  secao: SecaoCarregadaDoCertificado<T>,
  pagina: PaginaDaSecaoDoCertificado<T>,
): void {
  secao.items.push(...pagina.items);
  secao.total = pagina.total;
  secao.nextCursor = pagina.nextCursor;
  // Sem cursor não há como pedir a seguinte: pedir sem ele traria a primeira página de novo.
  secao.hasMore = pagina.hasMore && pagina.nextCursor !== null;
  secao.iniciada = true;
}

function acrescentarPaginaRecebida(
  secoes: SecoesDoCertificadoState,
  recebida: PaginaRecebidaDoCertificado,
): void {
  switch (recebida.secao) {
    case 'campos':
      return acrescentarPagina(secoes.campos, recebida.pagina);
    case 'contatos':
      return acrescentarPagina(secoes.contatos, recebida.pagina);
    case 'marcos':
      return acrescentarPagina(secoes.marcos, recebida.pagina);
    case 'mensagens':
      return acrescentarPagina(secoes.mensagens, recebida.pagina);
  }
}

/**
 * A emissão nova (outro `emissao.id`) descarta as páginas da anterior: o documento nunca mistura
 * duas versões. A mesma emissão de volta mantém o que já foi carregado.
 */
function aplicarEmissao(
  state: NegociacaoDetailState,
  requestId: string,
  certificado: CertificadoNegociacaoResumoDto,
): void {
  if (state.certificado.requestId !== requestId) return;
  state.certificado.loading = false;
  state.certificado.error = null;
  state.certificado.dados = certificado;
  if (state.certificado.secoes.emissaoId === certificado.emissao.id) return;
  state.certificado.secoes = secoesDaEmissao(certificado.emissao.id);
}

function aplicarFalhaDaEmissao(
  state: NegociacaoDetailState,
  requestId: string,
  erro: ErroDoCertificadoNegociacao | undefined,
): void {
  if (state.certificado.requestId !== requestId) return;
  state.certificado.loading = false;
  state.certificado.dados = null;
  state.certificado.error = erro ?? { status: null, code: null, fonte: null };
}

/**
 * A atualização em segundo plano que falha não troca o que está na tela: a emissão exibida, as
 * seções já carregadas ou o aviso que já estava lá. O 429 (30 consultas por minuto por pessoa e
 * caso) e a falha passageira se resolvem na próxima atualização. O 409 de vínculo a reconciliar,
 * não: é fato novo do caso, e a emissão exibida deixou de ser a definitiva (COR-05).
 */
function aplicarFalhaDaAtualizacao(
  state: NegociacaoDetailState,
  requestId: string,
  erro: ErroDoCertificadoNegociacao | undefined,
): void {
  const { certificado } = state;
  if (certificado.requestId !== requestId) return;
  const algoExibido = certificado.dados !== null || certificado.error !== null;
  if (algoExibido && !ehVinculoAReconciliar(erro ?? null)) {
    certificado.loading = false;
    return;
  }
  aplicarFalhaDaEmissao(state, requestId, erro);
}

/**
 * As conversas do caso que o store conhece: o canal ativo e o exibido no detalhe, a fila de
 * telefones do detalhe e a que o certificado pediu.
 */
function conversasConhecidas(
  state: NegociacaoDetailState,
  casoId: string,
): ConversasConhecidasDoCaso {
  const casoAberto = state.caso?.id === casoId;
  const filaDoDetalhe = state.telefones.casoIdBuscado === casoId ? state.telefones.items : [];
  const filaDoCertificado =
    state.certificado.conversasDaFila?.casoId === casoId
      ? state.certificado.conversasDaFila.conversaIds
      : [];
  const canais = casoAberto ? [state.conversaId, state.canalVisualizadoId] : [];
  return {
    casoId,
    conversaIds: [
      ...canais.flatMap((id) => (id ? [id] : [])),
      ...idsDasConversasDaFila(filaDoDetalhe),
      ...filaDoCertificado,
    ],
  };
}

function contarAcontecimentoDoCertificado(state: NegociacaoDetailState, ehDoCaso: boolean): void {
  if (ehDoCaso) state.certificado.acontecimentos += 1;
}

const initialState: NegociacaoDetailState = {
  caso: null,
  marcos: marcosVazios(),
  conversaId: null,
  canalVisualizadoId: null,
  telefones: { items: [], isLoading: false, error: null, casoIdBuscado: null },
  aiInsights: null,

  isLoadingAiInsights: false,
  aiInsightsError: null,

  loading: false,
  error: null,

  isCancellingStart: false,
  cancelStartError: null,

  isUpdating: false,
  updateError: null,

  isDownloadingReport: false,
  downloadReportError: null,

  isEncerrando: false,
  encerramentoError: null,
  isEncerrandoAtendimento: false,
  encerrarAtendimentoError: null,
  isReabrindo: false,
  reaberturaError: null,

  isResumingJanela: false,
  resumeJanelaError: null,
  resumeJanelaCaseId: null,

  lexiProcessing: false,
  lexiProcessingFailed: false,

  assinaturaAcordo: {
    casoId: null,
    requestId: null,
    data: null,
    status: 'idle',
    error: null,
  },

  fluxo: { flowId: null, requestId: null, data: null, status: 'idle' },

  certificado: {
    casoId: null,
    requestId: null,
    dados: null,
    loading: false,
    error: null,
    baixandoPdf: false,
    acontecimentos: 0,
    conversasDaFila: null,
    conversasAtualizadasEm: {},
    secoes: secoesDaEmissao(null),
  },

  outrasNegociacoes: {
    items: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    isLoading: false,
    error: null,
  },
};

const negociacaoDetailSlice = createSlice({
  name: 'negociacaoDetail',
  initialState,
  reducers: {
    clearNegociacaoDetailError: (state) => {
      state.error = null;
    },
    clearLexiProcessing: (state) => {
      state.lexiProcessing = false;
      state.lexiProcessingFailed = false;
    },
    /** Troca o canal exibido na aba Chat. `null` volta para o canal ativo do caso. */
    selecionarCanalVisualizado: (state, action: PayloadAction<string | null>) => {
      state.canalVisualizadoId = action.payload;
    },
    /**
     * Marco que nasce sem mensagem nem mudança de status, e o resultado do porteiro, não chegam
     * pelo tempo real: a volta à janela e o relógio da consulta aberta contam como acontecimento,
     * para seguirem o mesmo agrupamento e a mesma espera durante o download do PDF.
     */
    reconsultaDoCertificadoPedida: (state, action: PayloadAction<string>) => {
      contarAcontecimentoDoCertificado(state, action.payload === state.certificado.casoId);
    },
    updateNegociacaoCasoStatus: (
      state,
      action: PayloadAction<{ caseId: string; status: string }>,
    ) => {
      if (!state.caso || state.caso.id !== action.payload.caseId) return;
      state.caso.status = action.payload.status as NegociacaoCasoDetail['status'];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapNegociacaoDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(bootstrapNegociacaoDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.caso = action.payload.caso;
        state.conversaId = action.payload.conversaId;
        state.aiInsights = action.payload.aiInsights;
        // Trocar de caso reseta o canal visualizado e a fila: sem isto, a aba Chat abriria o
        // caso novo mostrando o canal do caso anterior.
        state.canalVisualizadoId = null;
        state.telefones = { items: [], isLoading: false, error: null, casoIdBuscado: null };
      })

      /**
       * Trocar de caso com a busca em voo não cancela a thunk: a resposta do caso A chega depois
       * de o bootstrap de B ter assumido o estado. Gravar a fila de A aí faz o seletor de canal
       * oferecer a conversa de A, e o composer envia para o telefone da pessoa errada — por isso
       * os três reducers descartam resposta que não é do caso ativo.
       */
      .addCase(fetchNegociacaoTelefones.pending, (state, action) => {
        if (state.caso?.id !== action.meta.arg) return;
        state.telefones.isLoading = true;
        state.telefones.error = null;
      })
      .addCase(fetchNegociacaoTelefones.fulfilled, (state, action) => {
        if (state.caso?.id !== action.meta.arg) return;
        state.telefones.isLoading = false;
        state.telefones.items = action.payload;
        state.telefones.casoIdBuscado = action.meta.arg;
      })
      .addCase(fetchNegociacaoTelefones.rejected, (state, action) => {
        if (state.caso?.id !== action.meta.arg) return;
        state.telefones.isLoading = false;
        state.telefones.casoIdBuscado = action.meta.arg;
        state.telefones.error =
          (action.payload as string) ||
          'Não foi possível carregar os outros números. Tente novamente.';
      })
      .addCase(bootstrapNegociacaoDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Erro ao carregar negociação';
      })

      .addCase(fetchNegociacaoCasoDetail.fulfilled, (state, action) => {
        state.caso = action.payload;
        state.aiInsights = action.payload.aiInsights ?? null;
      })

      .addCase(fetchAssinaturaAcordo.pending, (state, action) => {
        if (state.assinaturaAcordo.casoId !== action.meta.arg) {
          state.assinaturaAcordo.data = null;
        }
        state.assinaturaAcordo.casoId = action.meta.arg;
        state.assinaturaAcordo.requestId = action.meta.requestId;
        state.assinaturaAcordo.status = 'loading';
        state.assinaturaAcordo.error = null;
      })
      .addCase(fetchAssinaturaAcordo.fulfilled, (state, action) => {
        if (state.assinaturaAcordo.requestId !== action.meta.requestId) return;
        state.assinaturaAcordo.status = 'succeeded';
        state.assinaturaAcordo.data = action.payload;
      })
      .addCase(fetchAssinaturaAcordo.rejected, (state, action) => {
        if (state.assinaturaAcordo.requestId !== action.meta.requestId) return;
        state.assinaturaAcordo.status = 'failed';
        state.assinaturaAcordo.error =
          action.payload ?? 'Erro ao carregar status de assinatura do acordo';
      })

      .addCase(fetchFluxoDaNegociacao.pending, (state, action) => {
        if (state.fluxo.flowId !== action.meta.arg) state.fluxo.data = null;
        state.fluxo.flowId = action.meta.arg;
        state.fluxo.requestId = action.meta.requestId;
        state.fluxo.status = 'loading';
      })
      .addCase(fetchFluxoDaNegociacao.fulfilled, (state, action) => {
        if (state.fluxo.requestId !== action.meta.requestId) return;
        state.fluxo.status = 'succeeded';
        state.fluxo.data = action.payload;
      })
      .addCase(fetchFluxoDaNegociacao.rejected, (state, action) => {
        if (state.fluxo.requestId !== action.meta.requestId) return;
        state.fluxo.status = 'failed';
        state.fluxo.data = null;
      })

      .addCase(pauseNegociacaoCaso.fulfilled, (state, action) => {
        if (!state.caso) return;
        if (state.caso.id !== action.payload.caseId) return;
        state.caso.status = action.payload.status;
      })
      .addCase(resumeNegociacaoCaso.fulfilled, (state, action) => {
        if (!state.caso) return;
        if (state.caso.id !== action.payload.caseId) return;
        state.caso.status = action.payload.status;
      })
      .addCase(closeNegociacaoCaso.fulfilled, (state, action) => {
        if (!state.caso) return;
        if (state.caso.id !== action.payload.caseId) return;
        state.caso.status = action.payload.status;
      })

      .addCase(selecionarDirecionamento.pending, (state, action) => {
        if (!state.caso || state.caso.id !== action.meta.arg.caseId) return;
        state.lexiProcessing = true;
        state.lexiProcessingFailed = false;
      })
      .addCase(selecionarDirecionamento.fulfilled, (state, action) => {
        if (!state.caso || state.caso.id !== action.meta.arg.caseId) return;
        state.caso.pendencias = state.caso.pendencias.filter(
          (pendencia) => pendencia.id !== action.payload.pendenciaId,
        );
        if (action.payload.modeRestored) {
          state.caso.mode = 'BOT';
        } else {
          state.lexiProcessing = false;
        }
      })
      .addCase(selecionarDirecionamento.rejected, (state, action) => {
        if (!state.caso || state.caso.id !== action.meta.arg.caseId) return;
        state.lexiProcessing = false;
      })

      .addCase(resumeConversaAutomation.pending, (state, action) => {
        if (!state.caso || state.conversaId !== action.meta.arg.conversaId) return;
        state.lexiProcessing = true;
        state.lexiProcessingFailed = false;
      })
      .addCase(resumeConversaAutomation.rejected, (state, action) => {
        if (!state.caso || state.conversaId !== action.meta.arg.conversaId) return;
        state.lexiProcessing = false;
      })

      .addCase(direcionamentoPendenciaCreated, (state, action) => {
        if (!state.caso || state.caso.id !== action.payload.negociacaoCasoId) return;
        state.lexiProcessing = false;
        if (state.caso.pendencias.some((pendencia) => pendencia.id === action.payload.id)) return;
        state.caso.pendencias.push({
          id: action.payload.id,
          type: action.payload.type,
          title: action.payload.title,
          blocking: action.payload.blocking,
          createdAt: new Date().toISOString(),
          inputSchema: null,
          payload: action.payload.payload as DirecionamentoPayload | null,
          executionId: null,
          outputVariable: null,
        });
      })

      .addCase(direcionamentoResolved, (state, action) => {
        if (!state.caso || state.caso.id !== action.payload.negociacaoCasoId) return;
        state.lexiProcessing = false;
        state.lexiProcessingFailed = false;
      })
      .addCase(direcionamentoFailed, (state, action) => {
        if (!state.caso || state.caso.id !== action.payload.negociacaoCasoId) return;
        state.lexiProcessing = false;
        state.lexiProcessingFailed = true;
      })

      .addCase(fetchNegociacaoMarcos.pending, (state, action) => {
        const { caseId } = action.meta.arg;
        if (state.marcos.casoId !== caseId) {
          state.marcos = { ...marcosVazios(), casoId: caseId };
        }
        state.marcos.status = 'loading';
        state.marcos.error = null;
        state.marcos.requestId = action.meta.requestId;
      })
      .addCase(fetchNegociacaoMarcos.fulfilled, (state, action) => {
        if (state.marcos.requestId !== action.meta.requestId) return;
        const { cursor } = action.meta.arg;

        if (cursor) {
          state.marcos.items = [...state.marcos.items, ...action.payload.items];
          state.marcos.nextCursor = action.payload.nextCursor;
          state.marcos.hasMore = action.payload.hasMore;
        } else if (state.marcos.items.length === 0) {
          state.marcos.items = action.payload.items;
          state.marcos.nextCursor = action.payload.nextCursor;
          state.marcos.hasMore = action.payload.hasMore;
        } else {
          const idsRecebidos = new Set(action.payload.items.map((marco) => marco.id));
          state.marcos.items = [
            ...action.payload.items,
            ...state.marcos.items.filter((marco) => !idsRecebidos.has(marco.id)),
          ];
        }

        state.marcos.status = 'succeeded';
        state.marcos.error = null;
        state.marcos.tentativas = 0;
      })
      .addCase(fetchNegociacaoMarcos.rejected, (state, action) => {
        if (state.marcos.requestId !== action.meta.requestId) return;
        state.marcos.status = 'failed';
        state.marcos.error = action.payload ?? 'Erro ao carregar os marcos desta negociação';
        state.marcos.tentativas += 1;
      })

      .addCase(fetchNegociacaoAiInsights.pending, (state) => {
        state.isLoadingAiInsights = true;
        state.aiInsightsError = null;
      })
      .addCase(fetchNegociacaoAiInsights.fulfilled, (state, action) => {
        state.isLoadingAiInsights = false;
        state.aiInsights = action.payload;
      })
      .addCase(fetchNegociacaoAiInsights.rejected, (state, action) => {
        state.isLoadingAiInsights = false;
        state.aiInsightsError = (action.payload as string) || 'Erro ao carregar insights de IA';
      })

      .addCase(fetchNegociacaoConversaId.fulfilled, (state, action) => {
        state.conversaId = action.payload;
      })

      .addCase(cancelNegociacaoStart.pending, (state) => {
        state.isCancellingStart = true;
        state.cancelStartError = null;
      })
      .addCase(cancelNegociacaoStart.fulfilled, (state) => {
        state.isCancellingStart = false;
      })
      .addCase(cancelNegociacaoStart.rejected, (state, action) => {
        state.isCancellingStart = false;
        state.cancelStartError = (action.payload as string) || 'Erro ao cancelar início';
      })

      .addCase(updateNegociacaoCaso.pending, (state) => {
        state.isUpdating = true;
        state.updateError = null;
      })
      .addCase(updateNegociacaoCaso.fulfilled, (state, action) => {
        state.isUpdating = false;
        if (state.caso) {
          state.caso.titulo = action.payload.titulo;
          state.caso.claimAmount = action.payload.claimAmount;
          state.caso.floorAmount = action.payload.floorAmount;
          state.caso.marginPercent = action.payload.marginPercent;
          state.caso.counterpartyName = action.payload.counterpartyName;
          state.caso.counterpartyPhone = action.payload.counterpartyPhone;
          state.caso.counterpartyEmail = action.payload.counterpartyEmail;
          state.caso.scenarioType = action.payload.scenarioType;
          state.caso.dataAtualizacao = action.payload.dataAtualizacao;
        }
      })
      .addCase(updateNegociacaoCaso.rejected, (state, action) => {
        state.isUpdating = false;
        state.updateError = (action.payload as string) || 'Erro ao atualizar';
      })

      .addCase(downloadNegociacaoReport.pending, (state) => {
        state.isDownloadingReport = true;
        state.downloadReportError = null;
      })
      .addCase(downloadNegociacaoReport.fulfilled, (state) => {
        state.isDownloadingReport = false;
      })
      .addCase(downloadNegociacaoReport.rejected, (state, action) => {
        state.isDownloadingReport = false;
        state.downloadReportError = (action.payload as string) || 'Erro ao baixar relatório';
      })

      .addCase(encerrarNegociacaoDetail.pending, (state) => {
        state.isEncerrando = true;
        state.encerramentoError = null;
      })
      .addCase(encerrarNegociacaoDetail.fulfilled, (state, action) => {
        state.isEncerrando = false;
        if (state.caso && state.caso.id === action.payload.caseId) {
          state.caso.status = action.payload.status;
          state.caso.encerramento = {
            tipo: action.payload.tipoEncerramento,
            data: action.payload.dataEncerramento,
            motivo: action.payload.motivoEncerramento ?? null,
            responsavelId: action.payload.responsavelEncerramentoId ?? null,
            detalhesAcordo: (action.payload.detalhesAcordo as Record<string, unknown>) ?? null,
            permiteReabrir: action.payload.permiteReabrir,
          };
          if (action.meta.arg.encerrarAtendimentoHumano) {
            state.caso.atendimentoHumanoAtivo = false;
            state.caso.situacoes = state.caso.situacoes.filter(
              (situacao) => situacao.key !== SITUACAO_KEY_INTERVENCAO_HUMANA,
            );
          }
        }
      })
      .addCase(encerrarNegociacaoDetail.rejected, (state, action) => {
        state.isEncerrando = false;
        state.encerramentoError = (action.payload as string) || 'Erro ao encerrar';
      })

      .addCase(encerrarAtendimentoHumanoDetail.pending, (state) => {
        state.isEncerrandoAtendimento = true;
        state.encerrarAtendimentoError = null;
      })
      .addCase(encerrarAtendimentoHumanoDetail.fulfilled, (state, action) => {
        state.isEncerrandoAtendimento = false;
        if (state.caso && state.caso.id === action.payload.caseId) {
          state.caso.atendimentoHumanoAtivo = false;
          state.caso.situacoes = state.caso.situacoes.filter(
            (situacao) => situacao.key !== SITUACAO_KEY_INTERVENCAO_HUMANA,
          );
        }
      })
      .addCase(encerrarAtendimentoHumanoDetail.rejected, (state, action) => {
        state.isEncerrandoAtendimento = false;
        state.encerrarAtendimentoError =
          (action.payload as string) || 'Erro ao encerrar atendimento';
      })

      .addCase(reabrirNegociacaoDetail.pending, (state) => {
        state.isReabrindo = true;
        state.reaberturaError = null;
      })
      .addCase(reabrirNegociacaoDetail.fulfilled, (state, action) => {
        state.isReabrindo = false;
        if (state.caso && state.caso.id === action.payload.caseId) {
          state.caso.status = action.payload.status;
          state.caso.encerramento = null;
          state.caso.reabertura = {
            data: action.payload.dataReabertura,
            justificativa: action.payload.justificativaReabertura ?? null,
            responsavelId: action.payload.responsavelReaberturaId ?? null,
          };
        }
      })
      .addCase(reabrirNegociacaoDetail.rejected, (state, action) => {
        state.isReabrindo = false;
        state.reaberturaError = (action.payload as string) || 'Erro ao reabrir';
      })

      /**
       * Cada abertura pode trazer outra versão (decisão provisória 4): a emissão anterior sai já no
       * pending, para a tela não mostrar contagem nem documento de uma versão superada enquanto a
       * nova chega, e a resposta atrasada de outra abertura é descartada pelo requestId.
       */
      .addCase(obterEmissaoAtualDoCertificado.pending, (state, action) => {
        state.certificado.casoId = action.meta.arg;
        state.certificado.requestId = action.meta.requestId;
        state.certificado.dados = null;
        state.certificado.loading = true;
        state.certificado.error = null;
      })
      .addCase(obterEmissaoAtualDoCertificado.fulfilled, (state, action) => {
        aplicarEmissao(state, action.meta.requestId, action.payload);
      })
      .addCase(obterEmissaoAtualDoCertificado.rejected, (state, action) => {
        aplicarFalhaDaEmissao(state, action.meta.requestId, action.payload);
      })
      /**
       * Acontecimento novo com a consulta aberta: a emissão exibida (ou o aviso exibido) fica até a
       * resposta, que passa a ser a única válida (o `requestId` descarta a de um pedido anterior que
       * volte depois). Com o aviso na tela, a reconsulta a cada 120 s não pisca o carregando.
       */
      .addCase(atualizarEmissaoDoCertificado.pending, (state, action) => {
        const { dados, error } = state.certificado;
        const manterExibida =
          state.certificado.casoId === action.meta.arg && (dados !== null || error !== null);
        state.certificado.casoId = action.meta.arg;
        state.certificado.requestId = action.meta.requestId;
        if (manterExibida) return;
        state.certificado.dados = null;
        state.certificado.loading = true;
        state.certificado.error = null;
      })
      .addCase(atualizarEmissaoDoCertificado.fulfilled, (state, action) => {
        aplicarEmissao(state, action.meta.requestId, action.payload);
      })
      .addCase(atualizarEmissaoDoCertificado.rejected, (state, action) => {
        aplicarFalhaDaAtualizacao(state, action.meta.requestId, action.payload);
      })
      /** Só a página da emissão EXIBIDA entra: a de uma emissão superada é descartada. */
      .addCase(carregarPaginaDaSecaoDoCertificado.pending, (state, action) => {
        const { emissaoId } = action.meta.arg;
        if (state.certificado.dados?.emissao.id !== emissaoId) return;
        if (state.certificado.secoes.emissaoId !== emissaoId) {
          state.certificado.secoes = secoesDaEmissao(emissaoId);
        }
        state.certificado.secoes.requestId = action.meta.requestId;
        state.certificado.secoes.carregando = true;
        state.certificado.secoes.falhou = null;
      })
      .addCase(carregarPaginaDaSecaoDoCertificado.fulfilled, (state, action) => {
        const { secoes } = state.certificado;
        if (secoes.requestId !== action.meta.requestId) return;
        if (secoes.emissaoId !== action.meta.arg.emissaoId) return;
        secoes.carregando = false;
        acrescentarPaginaRecebida(secoes, action.payload);
      })
      .addCase(carregarPaginaDaSecaoDoCertificado.rejected, (state, action) => {
        const { secoes } = state.certificado;
        if (secoes.requestId !== action.meta.requestId) return;
        if (secoes.emissaoId !== action.meta.arg.emissaoId) return;
        secoes.carregando = false;
        secoes.falhou = action.meta.arg.secao;
      })
      .addCase(solicitarPdfDoCertificado.pending, (state) => {
        state.certificado.baixandoPdf = true;
      })
      .addCase(solicitarPdfDoCertificado.fulfilled, (state) => {
        state.certificado.baixandoPdf = false;
      })
      .addCase(solicitarPdfDoCertificado.rejected, (state) => {
        state.certificado.baixandoPdf = false;
      })

      .addCase(fetchOutrasNegociacoes.pending, (state) => {
        state.outrasNegociacoes.isLoading = true;
        state.outrasNegociacoes.error = null;
      })
      .addCase(fetchOutrasNegociacoes.fulfilled, (state, action) => {
        state.outrasNegociacoes.isLoading = false;
        state.outrasNegociacoes.items = action.payload.items;
        state.outrasNegociacoes.total = action.payload.total;
        state.outrasNegociacoes.page = action.payload.page;
        state.outrasNegociacoes.limit = action.payload.limit;
        state.outrasNegociacoes.totalPages = action.payload.totalPages;
      })
      .addCase(fetchOutrasNegociacoes.rejected, (state, action) => {
        state.outrasNegociacoes.isLoading = false;
        state.outrasNegociacoes.error =
          (action.payload as string) || 'Erro ao carregar outras negociações';
      })

      .addCase(sendResumeTemplate.pending, (state, action) => {
        state.isResumingJanela = true;
        state.resumeJanelaError = null;
        state.resumeJanelaCaseId = action.meta.arg;
      })
      .addCase(sendResumeTemplate.fulfilled, (state, action) => {
        if (state.resumeJanelaCaseId === action.payload.caseId) {
          state.isResumingJanela = false;
        }
        if (state.caso && state.caso.id === action.payload.caseId) {
          state.caso.janelaAtendimento = action.payload.janelaAtendimento;
        }
      })
      .addCase(sendResumeTemplate.rejected, (state, action) => {
        if (state.resumeJanelaCaseId !== action.meta.arg) return;
        state.isResumingJanela = false;
        state.resumeJanelaError =
          (action.payload as string) || 'Não foi possível enviar a mensagem. Tente novamente.';
      })

      .addCase(negociacaoCasoStatusUpdated, (state, action) => {
        if (!state.caso || state.caso.id !== action.payload.caseId) return;
        state.caso.status = action.payload.status as NegociacaoCasoDetail['status'];
      })

      /**
       * Os acontecimentos do caso que o tempo real e as ações do detalhe já entregam ao store. O
       * certificado não escuta o stream por conta própria: conta o que chega aqui e a consulta
       * aberta pede a emissão de novo (ACC-01). Só conta com a consulta do caso montada uma vez
       * (`certificado.casoId`).
       */
      .addMatcher(isAnyOf(fetchNegociacaoTelefones.fulfilled), (state, action) => {
        if (action.meta.arg !== state.certificado.casoId) return;
        state.certificado.conversasDaFila = {
          casoId: action.meta.arg,
          conversaIds: idsDasConversasDaFila(action.payload),
        };
      })
      .addMatcher(isAnyOf(addMensagem, updateMensagemStatus), (state, action) => {
        const { casoId } = state.certificado;
        if (!casoId) return;
        contarAcontecimentoDoCertificado(
          state,
          mensagemEhDoCaso(action.payload, conversasConhecidas(state, casoId)),
        );
      })
      .addMatcher(isAnyOf(updateConversa), (state, action) => {
        const { casoId, conversasAtualizadasEm } = state.certificado;
        if (!casoId) return;
        const { id, updatedAt } = action.payload;
        if (!conversasConhecidas(state, casoId).conversaIds.includes(id)) return;
        if (conversasAtualizadasEm[id] === updatedAt) return;
        conversasAtualizadasEm[id] = updatedAt;
        contarAcontecimentoDoCertificado(state, true);
      })
      .addMatcher(isAnyOf(negociacaoCasoStatusUpdated), (state, action) => {
        contarAcontecimentoDoCertificado(state, action.payload.caseId === state.certificado.casoId);
      })
      .addMatcher(
        isAnyOf(direcionamentoPendenciaCreated, direcionamentoResolved, direcionamentoFailed),
        (state, action) => {
          contarAcontecimentoDoCertificado(
            state,
            action.payload.negociacaoCasoId === state.certificado.casoId,
          );
        },
      )
      .addMatcher(
        isAnyOf(
          pauseNegociacaoCaso.fulfilled,
          resumeNegociacaoCaso.fulfilled,
          closeNegociacaoCaso.fulfilled,
          encerrarNegociacaoDetail.fulfilled,
          reabrirNegociacaoDetail.fulfilled,
        ),
        (state, action) => {
          contarAcontecimentoDoCertificado(
            state,
            action.meta.arg.caseId === state.certificado.casoId,
          );
        },
      )
      .addMatcher(isAnyOf(encerrarAtendimentoHumanoDetail.fulfilled), (state, action) => {
        contarAcontecimentoDoCertificado(state, action.meta.arg === state.certificado.casoId);
      });
  },
});

export const { clearLexiProcessing, reconsultaDoCertificadoPedida, selecionarCanalVisualizado } =
  negociacaoDetailSlice.actions;

export default negociacaoDetailSlice.reducer;
